import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'
import type { HubnaiBalance } from '@/services/api'

interface CreateHubnaiTransferModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface TransferResult {
  id: number
  hubnai_transfer_id: string
  amount: number
  status: string
  estimated_date?: string
}

const CreateHubnaiTransferModal: React.FC<CreateHubnaiTransferModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [balance, setBalance] = useState<HubnaiBalance | null>(null)
  const [loadingBalance, setLoadingBalance] = useState(true)
  const [transferResult, setTransferResult] = useState<TransferResult | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    amount: '', // Em reais
    description: '',
    // Dados bancários
    bank_code: '',
    bank_name: '',
    branch: '',
    branch_digit: '',
    account: '',
    account_digit: '',
    account_type: 'checking' as 'checking' | 'savings',
    // Titular
    holder_name: '',
    holder_document: ''
  })

  useEffect(() => {
    if (isOpen) {
      loadBalance()
    }
  }, [isOpen])

  const loadBalance = async () => {
    try {
      setLoadingBalance(true)
      const data = await hubnaiAPI.getBalance()
      setBalance(data)
    } catch (error: any) {
      console.error('Erro ao carregar saldo:', error)
    } finally {
      setLoadingBalance(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validar saldo disponível
    const amountValue = parseFloat(formData.amount)
    if (balance && amountValue > (balance.available / 100)) {
      alert('Valor superior ao saldo disponível!')
      return
    }

    try {
      setLoading(true)

      // Converter valor de reais para centavos
      const amountInCents = Math.round(amountValue * 100)

      // Preparar dados da transferência
      const transferData = {
        amount: amountInCents,
        description: formData.description || 'Saque via Gateway FinPro',
        bank_account: {
          bank_code: formData.bank_code,
          bank_name: formData.bank_name,
          branch: formData.branch,
          branch_digit: formData.branch_digit || '',
          account: formData.account,
          account_digit: formData.account_digit,
          account_type: formData.account_type,
          holder_name: formData.holder_name,
          holder_document: formData.holder_document.replace(/\D/g, '')
        }
      }

      console.log('📤 Criando transferência:', transferData)

      const result = await hubnaiAPI.createTransfer(transferData)

      console.log('✅ Transferência criada:', result)

      setTransferResult(result)

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar transferência:', error)
      alert('Erro ao criar transferência: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  const handleClose = () => {
    setTransferResult(null)
    setFormData({
      amount: '',
      description: '',
      bank_code: '',
      bank_name: '',
      branch: '',
      branch_digit: '',
      account: '',
      account_digit: '',
      account_type: 'checking',
      holder_name: '',
      holder_document: ''
    })
    onClose()
  }

  if (!isOpen) return null

  // Se já temos resultado, mostra confirmação
  if (transferResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Transferência Solicitada!</h2>
                  <p className="text-sm text-gray-500">ID: {transferResult.hubnai_transfer_id}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-semibold text-gray-900">{formatCurrency(transferResult.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-green-700 uppercase">{transferResult.status}</span>
              </div>
              {transferResult.estimated_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Previsão de crédito:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(transferResult.estimated_date).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>

            {/* Status Details */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                ✅ <strong>Transferência em processamento!</strong> O valor será creditado na conta bancária informada em até 1 dia útil.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Form para criar transferência
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <BanknotesIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Solicitar Saque</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Saldo Disponível */}
          {loadingBalance ? (
            <div className="bg-gray-100 rounded-lg p-4 animate-pulse">
              <div className="h-6 bg-gray-300 rounded w-1/2"></div>
            </div>
          ) : balance ? (
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Saldo Disponível para Saque</p>
                  <p className="text-2xl font-bold">{formatCurrency(balance.available)}</p>
                </div>
                <BanknotesIcon className="w-8 h-8 text-green-200" />
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Erro ao carregar saldo. Tente novamente.</p>
            </div>
          )}

          {/* Valor da Transferência */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Valor do Saque</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor (R$) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0.01"
                  max={balance ? (balance.available / 100) : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="100.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Valor máximo: {balance ? formatCurrency(balance.available) : 'Carregando...'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Saque para conta corrente"
                />
              </div>
            </div>
          </div>

          {/* Dados Bancários */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BuildingLibraryIcon className="w-5 h-5" />
              Dados Bancários de Destino
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código do Banco *
                </label>
                <input
                  type="text"
                  name="bank_code"
                  value={formData.bank_code}
                  onChange={handleChange}
                  required
                  maxLength={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Banco *
                </label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Banco do Brasil"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agência *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    name="branch"
                    value={formData.branch}
                    onChange={handleChange}
                    required
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1234"
                  />
                  <input
                    type="text"
                    name="branch_digit"
                    value={formData.branch_digit}
                    onChange={handleChange}
                    maxLength={1}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="X"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conta *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    name="account"
                    value={formData.account}
                    onChange={handleChange}
                    required
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="12345"
                  />
                  <input
                    type="text"
                    name="account_digit"
                    value={formData.account_digit}
                    onChange={handleChange}
                    required
                    maxLength={2}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Conta *
                </label>
                <select
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="checking">Conta Corrente</option>
                  <option value="savings">Conta Poupança</option>
                </select>
              </div>
            </div>
          </div>

          {/* Dados do Titular */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Titular</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo do Titular *
                </label>
                <input
                  type="text"
                  name="holder_name"
                  value={formData.holder_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="João da Silva"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ do Titular *
                </label>
                <input
                  type="text"
                  name="holder_document"
                  value={formData.holder_document}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="000.000.000-00"
                />
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Atenção:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Verifique todos os dados bancários antes de confirmar</li>
                <li>O prazo de crédito é de até 1 dia útil</li>
                <li>Transferências aprovadas não podem ser canceladas</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !balance}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processando...
                </>
              ) : (
                <>
                  <BanknotesIcon className="w-5 h-5" />
                  Solicitar Saque
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateHubnaiTransferModal
